import { gsap } from "gsap";

export default class FiltersHandler {
    constructor() {
        // Detect post type from data attribute on posts container (for Taxi.js compatibility)
        const postsContainer = document.querySelector('[data-posts-container]');
        const detectedPostType = postsContainer?.dataset.postType || window.filterAjax?.post_type || 'cpt-articles';

        this.isSearchMode = !!(postsContainer?.dataset.searchQuery);
        this.searchQuery = postsContainer?.dataset.searchQuery || '';

        this.currentFilters = {
            post_type: detectedPostType,
            taxonomies: {},
            paged: 1,
            posts_per_page: this.getPostsPerPage(detectedPostType),
            exclude_posts: []
        };
        this.isLoading = false;
        this.ajaxUrl = window.filterAjax?.ajaxurl || '/wp-admin/admin-ajax.php';
        this.nonce = window.filterAjax?.nonce || '';
        this.initialPostType = this.currentFilters.post_type;
        this.getFeaturedPosts();
    }

    getPostsPerPage(postType) {
        if (postType === 'cpt-articles') return 9;
        if (postType === 'cpt-resources') return 9;
        if (postType === 'cpt-events') return 6;
        return 9;
    }

    getFeaturedPosts() {
        const postsContainer = document.querySelector('[data-posts-container]');
        if (postsContainer && postsContainer.dataset.excludePosts) {
            try {
                this.currentFilters.exclude_posts = JSON.parse(postsContainer.dataset.excludePosts);
            } catch (e) {
                console.error('Failed to parse exclude_posts:', e);
            }
        }
    }

    init() {
        const hasFilters = document.querySelector('[data-taxonomy-select]');
        const postsContainer = document.querySelector('[data-posts-container]');
        const hasPagination = document.querySelector('[data-pagination-container]');

        if (!postsContainer) {
            return;
        }

        if (postsContainer && postsContainer.dataset.postType) {
            this.currentFilters.post_type = postsContainer.dataset.postType;
            this.initialPostType = postsContainer.dataset.postType;
            this.currentFilters.posts_per_page = this.getPostsPerPage(postsContainer.dataset.postType);
        }

        this.checkPostTypeChange();
        this.initTaxonomySelects();
        this.initPopState();
        this.initFromURL();
        this.initPaginationHandlers();
    }

    checkPostTypeChange() {
        const postsContainer = document.querySelector('[data-posts-container]');
        const currentPostType = postsContainer?.dataset.postType || window.filterAjax?.post_type || 'cpt-articles';

        if (this.initialPostType !== currentPostType) {
            this.resetFilters();
            this.initialPostType = currentPostType;
            this.currentFilters.post_type = currentPostType;
        }
    }

    resetFilters() {
        const postsContainer = document.querySelector('[data-posts-container]');
        const detectedPostType = postsContainer?.dataset.postType || window.filterAjax?.post_type || 'cpt-articles';

        this.currentFilters = {
            post_type: detectedPostType,
            taxonomies: {},
            paged: 1,
            posts_per_page: this.getPostsPerPage(detectedPostType),
            exclude_posts: []
        };

        this.getFeaturedPosts();
        this.clearActiveFilters();

        if (window.location.search) {
            const cleanURL = window.location.pathname;
            window.history.replaceState({ filters: this.currentFilters }, '', cleanURL);
        }
    }

    initTaxonomySelects() {
        const taxonomySelects = document.querySelectorAll('[data-taxonomy-select]');

        taxonomySelects.forEach(select => {
            select.addEventListener('change', (e) => {
                const taxonomy = e.target.getAttribute('data-taxonomy-select');
                const termSlug = e.target.value;

                if (termSlug) {
                    this.currentFilters.taxonomies[taxonomy] = termSlug;
                } else {
                    delete this.currentFilters.taxonomies[taxonomy];
                }

                this.currentFilters.paged = 1;
                this.executeFilter();
            });
        });
    }

    initPopState() {
        window.addEventListener('popstate', (e) => {
            if (e.state && e.state.filters) {
                this.currentFilters = { ...e.state.filters };
                this.executeFilter(false);
                this.updateUIFromFilters();
            }
        });
    }

    initFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        const paged = parseInt(urlParams.get('paged')) || 1;

        // Check for taxonomy parameters
        const categorySlug = urlParams.get('category');
        const topicSlug = urlParams.get('topic');

        if (categorySlug) {
            if (this.currentFilters.post_type === 'cpt-articles') {
                this.currentFilters.taxonomies['tax-article-category'] = categorySlug;
            } else if (this.currentFilters.post_type === 'cpt-events') {
                this.currentFilters.taxonomies['tax-event-category'] = categorySlug;
            } else if (this.currentFilters.post_type === 'cpt-resources') {
                this.currentFilters.taxonomies['tax-resource-category'] = categorySlug;
            }
        }

        if (topicSlug && this.currentFilters.post_type === 'cpt-resources') {
            this.currentFilters.taxonomies['tax-resource-topic'] = topicSlug;
        }

        this.currentFilters.paged = paged;

        if (Object.keys(this.currentFilters.taxonomies).length > 0 || paged > 1) {
            if (!this.isSearchMode && document.querySelector('[data-posts-container]')) {
                this.executeFilter(false);
                this.updateUIFromFilters();
            }
        }
    }

    clearActiveFilters() {
        const taxonomySelects = document.querySelectorAll('[data-taxonomy-select]');
        taxonomySelects.forEach(select => {
            select.value = '';
        });
    }

    updateUIFromFilters() {
        Object.entries(this.currentFilters.taxonomies).forEach(([taxonomy, termSlug]) => {
            const select = document.querySelector(`[data-taxonomy-select="${taxonomy}"]`);
            if (select) {
                select.value = termSlug;
            }
        });
    }

    executeFilter(updateURL = true) {
        if (this.isLoading) return;

        this.isLoading = true;
        this.showLoadingState();

        const formData = new FormData();
        formData.append('nonce', this.nonce);
        formData.append('paged', this.currentFilters.paged);

        if (this.isSearchMode) {
            formData.append('action', 'search_posts');
            formData.append('s', this.searchQuery);
        } else {
            formData.append('action', 'filter_posts');
            formData.append('post_type', this.currentFilters.post_type);
            formData.append('posts_per_page', this.currentFilters.posts_per_page);
            formData.append('taxonomies', JSON.stringify(this.currentFilters.taxonomies));

            if (this.currentFilters.exclude_posts && this.currentFilters.exclude_posts.length > 0) {
                this.currentFilters.exclude_posts.forEach(id => {
                    formData.append('exclude_posts[]', id);
                });
            }
        }

        fetch(this.ajaxUrl, {
            method: 'POST',
            body: formData
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                this.updateResults(data.data);
                if (updateURL) {
                    this.updateURL();
                }
                this.updateUIFromFilters();
            } else {
                console.error('Filter request failed:', data);
            }
        })
        .catch(error => {
            console.error('AJAX error:', error);
        })
        .finally(() => {
            this.isLoading = false;
            this.hideLoadingState();
        });
    }

    showLoadingState() {
        const container = document.querySelector('[data-posts-container]');
        if (container) {
            container.style.opacity = '0.5';
            container.style.pointerEvents = 'none';
        }
    }

    hideLoadingState() {
        const container = document.querySelector('[data-posts-container]');
        if (container) {
            gsap.to(container, { opacity: 1, duration: 0.3 });
            container.style.pointerEvents = 'auto';
        }
    }

    updateResults(data) {
        const container = document.querySelector('[data-posts-container]');
        if (!container) return;

        gsap.to(container, {
            opacity: 0,
            duration: 0.2,
            onComplete: () => {
                container.innerHTML = data.posts.join('');

                // Use server-rendered pagination HTML if available
                if (data.pagination_html) {
                    const paginationContainer = document.querySelector('[data-pagination-container]');
                    if (paginationContainer) {
                        paginationContainer.innerHTML = data.pagination_html;
                        this.initPaginationHandlers();
                    }
                } else {
                    // Fallback to JS-generated pagination
                    this.updatePagination(data.pagination);
                }

                gsap.fromTo(container,
                    { opacity: 0 },
                    { opacity: 1, duration: 0.3 }
                );
            }
        });
    }

    updatePagination(pagination) {
        const paginationContainer = document.querySelector('[data-pagination-container]');
        if (!paginationContainer) return;

        if (!pagination || pagination.total_pages <= 1) {
            paginationContainer.innerHTML = '';
            return;
        }

        const currentPage = pagination.current_page;
        const totalPages = pagination.total_pages;
        const showDots = totalPages > 5;

        let html = '';

        // Previous button
        if (currentPage > 1) {
            html += `<a href="#" data-page="${currentPage - 1}" class="flex h-12 w-12 justify-center p-xs rounded-xxl border-0 outline-0 bg-blue-1 items-center cursor-pointer hover:bg-blue-2 transition">
                <div class="w-4 h-4 rotate-180 text-white">
                    <svg width="7" height="10" viewBox="0 0 7 10" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 9L5 5L1 1" stroke-width="2"></path>
                    </svg>
                </div>
            </a>`;
        }

        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, currentPage + 2);

        // First page + dots
        if (showDots && start > 1) {
            html += `<a href="#" data-page="1" class="flex h-12 w-12 justify-center rounded-xxl border-0 outline-0 items-center cursor-pointer ${currentPage === 1 ? 'bg-purple hover:bg-blue-2 transition' : 'border border-grey-2 bg-transparent hover:bg-grey-2 transition'}">
                <p class="${currentPage === 1 ? 'text-white' : 'text-blue-1'} leading-none text-center">1</p>
            </a>`;
            if (start > 2) {
                html += `<div class="flex h-12 w-12 justify-center rounded-xxl border border-grey-2 outline-0 bg-transparent items-center">
                    <p class="text-blue-1 leading-none text-center">...</p>
                </div>`;
            }
        }

        // Page numbers
        for (let i = start; i <= end; i++) {
            html += `<a href="#" data-page="${i}" class="flex h-12 w-12 justify-center rounded-xxl border-0 outline-0 items-center cursor-pointer ${currentPage === i ? 'bg-purple hover:bg-blue-2 transition' : 'border border-grey-2 bg-transparent hover:bg-grey-2 transition'}">
                <p class="${currentPage === i ? 'text-white' : 'text-blue-1'} leading-none text-center">${i}</p>
            </a>`;
        }

        // Dots + last page
        if (showDots && end < totalPages) {
            if (end < totalPages - 1) {
                html += `<div class="flex h-12 w-12 justify-center rounded-xxl border border-grey-2 outline-0 bg-transparent items-center">
                    <p class="text-blue-1 leading-none text-center">...</p>
                </div>`;
            }
            html += `<a href="#" data-page="${totalPages}" class="flex h-12 w-12 justify-center rounded-xxl border-0 outline-0 items-center cursor-pointer ${currentPage === totalPages ? 'bg-purple hover:bg-blue-2 transition' : 'border border-grey-2 bg-transparent hover:bg-grey-2 transition'}">
                <p class="${currentPage === totalPages ? 'text-white' : 'text-blue-1'} leading-none text-center">${totalPages}</p>
            </a>`;
        }

        // Next button
        if (currentPage < totalPages) {
            html += `<a href="#" data-page="${currentPage + 1}" class="flex h-12 w-12 justify-center p-xs rounded-xxl border-0 outline-0 bg-blue-1 items-center cursor-pointer hover:bg-blue-2 transition">
                <div class="w-4 h-4 text-white">
                    <svg width="7" height="10" viewBox="0 0 7 10" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 9L5 5L1 1" stroke-width="2"></path>
                    </svg>
                </div>
            </a>`;
        }

        paginationContainer.innerHTML = html;
        this.initPaginationHandlers();
    }

    initPaginationHandlers() {
        const paginationContainer = document.querySelector('[data-pagination-container]');
        if (!paginationContainer) return;

        if (this.paginationClickHandler) {
            paginationContainer.removeEventListener('click', this.paginationClickHandler);
        }

        this.paginationClickHandler = (e) => {
            const link = e.target.closest('[data-page]');

            if (link && link.hasAttribute('data-page')) {
                e.preventDefault();
                const page = parseInt(link.getAttribute('data-page'));

                if (page && page !== this.currentFilters.paged) {
                    this.currentFilters.paged = page;
                    this.executeFilter();

                    const postsContainer = document.querySelector('[data-posts-container]');
                    if (postsContainer) {
                        const parentContainer = postsContainer.closest('.container');
                        if (parentContainer) {
                            const offset = 100;
                            const elementPosition = parentContainer.getBoundingClientRect().top + window.pageYOffset;
                            window.scrollTo({
                                top: elementPosition - offset,
                                behavior: 'smooth'
                            });
                        }
                    }
                }
            }
        };

        paginationContainer.addEventListener('click', this.paginationClickHandler);
    }

    updateURL() {
        const params = new URLSearchParams();

        Object.entries(this.currentFilters.taxonomies).forEach(([taxonomy, termSlug]) => {
            // Articles use tax-article-category -> ?category=
            if (taxonomy === 'tax-article-category') {
                params.set('category', termSlug);
            }
            // Events use tax-event-category -> ?category=
            else if (taxonomy === 'tax-event-category') {
                params.set('category', termSlug);
            }
            // Resources use tax-resource-category -> ?category= and tax-resource-topic -> ?topic=
            else if (taxonomy === 'tax-resource-category') {
                params.set('category', termSlug);
            }
            else if (taxonomy === 'tax-resource-topic') {
                params.set('topic', termSlug);
            }
        });

        if (this.currentFilters.paged > 1) {
            params.set('paged', this.currentFilters.paged);
        }

        const newURL = window.location.pathname + (params.toString() ? '?' + params.toString() : '');

        window.history.pushState(
            { filters: this.currentFilters },
            '',
            newURL
        );
    }
}
